import { useState } from "react";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { ALL_TIERS, SCENARIOS, projectYear, usd } from "./data";

const COLORS = { conservative: "#B9A77F", base: "#8F6B22", strong: "#C9A24A" };
const tip = { background: "#1E1A14", border: "1px solid rgba(201,162,74,.3)", color: "#EDE6D6", fontSize: 13 };

export const GtaxProjection = () => {
  const [amt, setAmt] = useState(100000);
  const eligible = ALL_TIERS.filter((t) => amt >= t.min);
  const [tierId, setTierId] = useState("vip2");
  const tier = eligible.find((t) => t.id === tierId) || eligible[eligible.length - 1] || ALL_TIERS[0];
  const data = projectYear(amt, tier);
  const end = data[12];
  return (
    <section className="py-28 gx-line" data-testid="gtax-projection">
      <div className="gx-wrap">
        <p className="gx-eyebrow">Twelve-month illustration</p>
        <h2 className="gx-serif text-4xl md:text-5xl mt-6 max-w-3xl">Three scenarios across a full year, net to the member.</h2>
        <p className="mt-6 max-w-3xl text-[color:var(--mute)]">
          Hypothetical illustration only. Scenarios assume an average realised gross result per trading day (losing days included) of
          0.30%, 0.80% and 1.20%, sitting inside the strategy's historical daily range of 0.3%–2.1%. Figures are net of all strategy,
          execution and introducer costs and the annual licence, with distributions taken monthly (no compounding). Actual results will differ and may be negative.
        </p>
        <div className="mt-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-8">
            <div>
              <label className="block text-xs tracking-widest uppercase text-[color:var(--mute)]">Allocation (USD)</label>
              <input type="number" min={1000} step={1000} value={amt} onChange={(e) => setAmt(Number(e.target.value) || 0)}
                className="gx-serif text-4xl w-full py-3 mt-2" data-testid="gtax-proj-input" />
            </div>
            <div>
              <div className="text-xs tracking-widest uppercase text-[color:var(--mute)] mb-3">Membership</div>
              <div className="flex flex-wrap gap-2">
                {ALL_TIERS.map((t) => (
                  <button key={t.id} disabled={amt < t.min} onClick={() => setTierId(t.id)}
                    className={`gx-ghost !px-4 !py-2 disabled:opacity-30 ${tier.id === t.id ? "!border-[color:var(--gold)] !text-[color:var(--gold2)]" : ""}`}
                    data-testid={`gtax-proj-tier-${t.id}`}>{t.name}</button>
                ))}
              </div>
            </div>
            {amt < ALL_TIERS[0].min ? (
              <p className="text-sm text-[#A0523F]" data-testid="gtax-proj-min-notice">Minimum allocation is {usd(ALL_TIERS[0].min)}.</p>
            ) : (
            <div className="gx-card divide-y divide-[color:var(--line)]">
              {SCENARIOS.map((s) => (
                <div key={s.id} className="flex justify-between p-5" data-testid={`gtax-proj-end-${s.id}`}>
                  <div>
                    <div className="gx-serif text-xl">{s.name}</div>
                    <div className="text-xs text-[color:var(--mute)]">{s.d.toFixed(2)}% avg gross / day</div>
                  </div>
                  <div className="text-right">
                    <div className="gx-serif text-2xl gx-gold">{usd(end[s.id])}</div>
                    <div className="text-xs text-[color:var(--mute)]">{usd(end[s.id] - amt)} net, year 1</div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
          <div className="lg:col-span-8 gx-card p-6 h-[460px]" data-testid="gtax-proj-chart">
            <ResponsiveContainer>
              <LineChart data={data}>
                <XAxis dataKey="m" stroke="#6B5F4B" tickLine={false} axisLine={false} fontSize={13} />
                <YAxis stroke="#6B5F4B" tickLine={false} axisLine={false} fontSize={13} width={70} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                <Tooltip contentStyle={tip} formatter={(v) => usd(v)} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                {SCENARIOS.map((s) => (
                  <Line key={s.id} type="monotone" dataKey={s.id} name={s.name} stroke={COLORS[s.id]} strokeWidth={2.5} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </section>
  );
};
